<template lang="pug">
loader(v-if="loading")
template(v-else-if="errorMessage")
  .text-center.p-2
    h1.h3 {{ errorMessage }}
template(v-else)
  .container-fluid.p-2
    .text-center
      h1.fw-bolder Contracts
  contracts-chart
  .container-fluid
    .row(v-if="contracts && botsInfo")
      .col-12.col-sm-6.col-lg-4.col-xl-3.mb-3
        a.new-contract.text-decoration-none.d-flex.rounded.border.text-secondary.p-3.h-100.zoom(
          href="#",
          data-bs-toggle='modal',
          data-bs-target='#openContract',
          @click="createNewContract"
        )
          .mx-auto.my-auto.text-center
            font-awesome-icon.fa-2x.m-2(:icon="['far','plus-circle']")
            p Add a contract
      .col-12.col-sm-6.col-lg-4.col-xl-3.mb-3(v-for="contract in contracts")
        .shadow-sm.rounded.p-4.h-100.bg-white.zoom.d-flex.flex-column
          .position-relative
            h5
              a.text-decoration-none.stretched-link(
                href="#",
                style="height: 12rem",
                data-bs-toggle='modal',
                data-bs-target='#openContract',
                :class="{'link-danger': !contract.companyName}"
                @click="selectContract(contract.id)"
              )
                font-awesome-icon.me-1(:icon="['far','file-alt']")
                | {{ contract.companyName || contract.code }}
            ul.list-unstyled
              li(v-for="rule in contract.contractRules")
                img.me-1.align-baseline(
                  :src="getIconSrcByPlatform(rule.platform)",
                  height="12.8",
                  width="12.8"
                )
                span.font-monospace.small.text-muted.me-1 {{ rule.botId }}
                | {{ rule.name }}
          .small.mt-auto {{ `Expiry date ${formatDate(contract.endDate)}` }}

contract-modal(
  modal-id="openContract",
  :contract="contractModal.value",
  :chatbots="contractModal.chatbots",
  :contracts-history="contractModal.contractsHistory",
  @save="onContractSave",
  @hidden="onModalHide",
  @changeEmail="onChangeEmail"
)

</template>

<script>
import Loader from '@/components/base/Loader'
import ContractsChart from '@/components/pages/contracts/ContractsChart'
import ContractModal from '@/components/ContractModal'

import { getBotsByEmail } from '@/services/api/bot.api'
import {
  getContractsByEmail,
  saveContractChanges,
  getAllContracts,
  addContract
} from '@/services/api/contract.api'
import { createContract } from '@/utils/factory/contract/index'
import { toHumanDate } from '@/utils/date'
import { getNameByCode, getIconSrcByPlatform } from '@/utils/helpers/contract'

const ContractModalMode = {
  EDIT: 'editMode',
  CREATE: 'createMode'
}

export default {
  components: { Loader, ContractModal, ContractsChart },
  data() {
    return {
      loading: true,
      errorMessage: '',
      contracts: null,
      shoulNotSaveContract: false,
      allBots: [],
      botsByEmail: {},
      botsInfo: null,
      contractModal: {
        mode: ContractModalMode.EDIT,
        value: null,
        chatbots: [],
        contractsHistory: []
      },
      companiesInfo: {},
      chatbots: {}
    }
  },
  async created() {
    const clientToken = this.$cookie.get('clientToken')
    if (!clientToken) window.location.replace('/')
    try {
      await this.fetchContracts()
      const emails = this.getEmails()
      await this.prepareBotsInfo(emails)
      this.extendContractRulesInfo(this.contracts)
      setTimeout(() => {
        this.loading = false
      }, 500)
    } catch (err) {
      this.loading = false
      this.errorMessage = err.message
    }
  },
  methods: {
    getIconSrcByPlatform,
    createNewContract() {
      const newContract = createContract()
      this.contractModal.value = newContract
      this.contractModal.mode = ContractModalMode.CREATE
      this.contractModal.chatbots = []
      this.contractModal.contractsHistory = []
    },
    async selectContract(contractId) {
      this.contractModal.value = this.contracts.find(
        (contract) => contract.id === contractId
      )
      this.contractModal.mode = ContractModalMode.EDIT
      this.contractModal.chatbots =
        this.chatbots[this.contractModal.value.email]
      this.contractModal.contractsHistory = await this.getContractsHistory(
        this.contractModal.value.email
      )
    },
    formatDate(date) {
      return toHumanDate(date)
    },
    async onContractSave({ contract, id }) {
      if (this.shoulNotSaveContract) {
        this.shoulNotSaveContract = false
        return
      }
      const clientToken = this.$cookie.get('clientToken')
      if (this.contractModal.mode === ContractModalMode.EDIT) {
        await saveContractChanges({
          id,
          changes: contract,
          clientToken
        })
      } else {
        await addContract({ contract, clientToken })
      }

      await this.fetchContracts()
      this.extendContractRulesInfo(this.contracts)
      setTimeout(() => {
        this.loading = false
      }, 500)
    },
    async fetchContracts() {
      this.loading = true
      this.contracts = await this.getContracts()
      this.contracts = this.contracts.sort(
        (a, b) => b.contractDate.getTime() - a.contractDate.getTime()
      )
      try {
        await this.prepareCompaniesInfo()
      } catch (err) {
        console.warn(`Couldn't fetch companies info: ${err}`)
      }
    },
    extendContractRulesInfo(contracts) {
      contracts.forEach((contract, index) => {
        const companyName =
          this.companiesInfo[contract.code] &&
          this.companiesInfo[contract.code].name
        contracts[index].setCompanyName(companyName)

        contract.contractRules.forEach((rule, ruleIndex) => {
          if (this.botsInfo[rule.botId]) {
            const name = this.botsInfo[rule.botId].name
            const platform = this.botsInfo[rule.botId].platform
            contract.contractRules[ruleIndex].setPlatform(platform)
            contract.contractRules[ruleIndex].setName(name)
          }
        })
      })
    },
    onModalHide(event) {
      const result = confirm('Do you want to close dialog?')
      if (!result) {
        event.preventDefault()
        this.shoulNotSaveContract = true
      }
    },
    async onChangeEmail(email) {
      if (!this.chatbots[email]) {
        await this.prepareBotsInfo([email])
        this.extendContractRulesInfo(this.contracts)
      }
      this.contractModal.chatbots = this.chatbots[email]
      this.contractModal.contractsHistory = await this.getContractsHistory(
        email
      )
      this.extendContractRulesInfo(this.contractModal.contractsHistory)
    },
    getEmails() {
      return [...new Set(this.contracts.map(({ email }) => email))]
    },
    async prepareBotsInfo(emails) {
      const botsByEmail = await this.getAllBotsByEmails(emails)
      Object.keys(botsByEmail).forEach((email) => {
        this.chatbots[email] = botsByEmail[email].map(
          ({ id, name, messenger }) => ({
            id,
            name,
            platform: messenger
          })
        )
      })
      this.botsInfo = Object.values(this.chatbots)
        .flat()
        .reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {})
    },
    async prepareCompaniesInfo() {
      const codes = [...new Set(this.contracts.map(({ code }) => code))]
      const promises = codes.map(this.prepareCompanyInfoIfNeeded)
      await Promise.all(promises)
    },
    async prepareCompanyInfoIfNeeded(code) {
      if (!this.companiesInfo[code]) {
        const companyName = await getNameByCode(code)
        this.companiesInfo[code] = { name: companyName }
      }
    },
    async getAllBotsByEmails(emails) {
      const clientToken = this.$cookie.get('clientToken')
      let bots = {}

      for await (let email of emails) {
        try {
          let { data } = await getBotsByEmail({
            clientToken,
            email
          })
          bots[email] = data.map((bot) => bot.attributes)
        } catch (err) {
          console.warn(`Couldn't fetch user bots by email ${email}: ${err}`)
        }
      }
      return bots
    },
    async getContracts() {
      const clientToken = this.$cookie.get('clientToken')
      const { data } = await getAllContracts({
        clientToken
      })
      return data.map((contract) => createContract(contract.attributes))
    },
    async getContractsHistory(email) {
      const clientToken = this.$cookie.get('clientToken')
      let { data } = await getContractsByEmail({
        email,
        clientToken
      })
      let history = data.map((contract) => createContract(contract.attributes))
      const codes = [...new Set(history.map(({ code }) => code))]
      const promises = codes.map(this.prepareCompanyInfoIfNeeded)

      await Promise.all(promises)

      history.forEach((contract) => {
        const code = contract.code
        const companyName =
          this.companiesInfo[code] && this.companiesInfo[code].name
        contract.setCompanyName(companyName || code)
      })
      return history
    }
  }
}
</script>

<style lang="sass" scoped>
svg
  vertical-align: -10%

.new-contract
  cursor: pointer

.zoom
  transition: transform 0.125s ease-out
  &:hover
    transform: scale(1.025)
</style>
